import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import {
  GET_REGRAS_AVALIACAO,
  ADD_REGRAS_AVALIACAO,
  UPDATE_REGRAS_AVALIACAO,
  DELETE_REGRAS_AVALIACAO,
  BUSCA_REGRAS_AVALIACAO_EDICAO,
  ON_HIDE_LOADER
} from '../constants/ActionTypes';
import {
  getRegrasAvaliacaoSuccess,
  addRegrasAvaliacaoSuccess,
  updateRegrasAvaliacaoSuccess,
  deleteRegrasAvaliacaoSuccess,
  buscaRegrasAvaliacaoEdicaoSuccess,
  showMessage,
  showSuccessMessage
} from '../actions/RegrasAvaliacao';
import api from '../util/Api';

const getRegrasAvaliacaoRequest = async (filter) => {
  const params = typeof filter === 'string' ? { Nome: filter } : filter;
  return await api.get('regras-avaliacao', { params })
    .then(res => res.data)
    .catch(error => error);
}

const addRegrasAvaliacaoRequest = async (data) => {
  return await api.post('regras-avaliacao', data);
}

const updateRegrasAvaliacaoRequest = async (data) => {
  return await api.put(`regras-avaliacao/${data.id}`, data);
}

const deleteRegrasAvaliacaoRequest = async (id) => {
  return await api.delete(`regras-avaliacao/${id}`);
}

const getRegrasAvaliacaoEdicaoRequest = async (id) => {
  return await api.get(`regras-avaliacao/${id}`)
    .then(res => res.data)
    .catch(error => error);
}

// Função helper para extrair mensagens de erro do backend seguindo o padrão do ArredondamentoModal
const extractErrorMessage = (error, mensagemPadrao) => {
  if (!error) return mensagemPadrao;

  const response = error.response;
  const status = response?.status;
  const responseData = response?.data;

  // Se for erro 400 (validação), tentar extrair inconsistências
  if (status === 400 && responseData) {
    const inconsistencias = responseData.inconsistencias || [];
    if (Array.isArray(inconsistencias) && inconsistencias.length > 0) {
      return inconsistencias[0].mensagem || mensagemPadrao;
    }
    if (responseData.mensagem) {
      return responseData.mensagem;
    }
    if (responseData.message) {
      return responseData.message;
    }
  }

  // Tentar outras formas de extrair a mensagem
  if (responseData?.mensagem) return responseData.mensagem;
  if (responseData?.message) return responseData.message;
  if (error.message) return error.message;

  return mensagemPadrao;
};

function* getRegrasAvaliacao({ payload }) {
  try {
    const data = yield call(getRegrasAvaliacaoRequest, payload);
    // Verifica se a resposta é um erro (tem message) ou dados válidos
    if (data && data.message && !Array.isArray(data)) {
      yield put(showMessage(data.message));
    } else {
      // A API pode retornar diretamente um array ou um objeto com a lista dentro de um campo
      const lista = Array.isArray(data)
        ? data
        : (data?.regrasAvaliacao || data?.RegrasAvaliacao || data?.data || []);
      yield put(getRegrasAvaliacaoSuccess(lista));
    }
  } catch (error) {
    yield put(showMessage('Erro ao buscar regras de avaliação'));
  } finally {
    // Garante que o loader seja desligado mesmo em caso de erro
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* addRegrasAvaliacao({ payload }) {
  try {
    const response = yield call(addRegrasAvaliacaoRequest, payload);
    // Se chegou aqui, é sucesso (axios lança exceção para status >= 400)
    const data = response.data || response;

    // Normalização defensiva para garantir que o id esteja presente (backend pode retornar Id ou id)
    if (data && !data.id && data.Id) {
      data.id = data.Id;
    }

    yield put(addRegrasAvaliacaoSuccess(data));
    yield put(showSuccessMessage('regrasAvaliacao.sucessoCadastro'));
  } catch (error) {
    // Qualquer erro (400, 500, rede) cai aqui
    const mensagemErro = extractErrorMessage(error, 'Erro ao cadastrar regra de avaliação');
    yield put(showMessage(mensagemErro));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* updateRegrasAvaliacao({ payload }) {
  try {
    const response = yield call(updateRegrasAvaliacaoRequest, payload);
    // Se chegou aqui, é sucesso
    const data = response.data || response;
    yield put(updateRegrasAvaliacaoSuccess(data));
    yield put(showSuccessMessage('regrasAvaliacao.sucessoAtualizacao'));
  } catch (error) {
    const mensagemErro = extractErrorMessage(error, 'Erro ao atualizar regra de avaliação');
    yield put(showMessage(mensagemErro));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* deleteRegrasAvaliacao({ payload }) {
  try {
    yield call(deleteRegrasAvaliacaoRequest, payload);
    // Se chegou aqui, é sucesso
    yield put(deleteRegrasAvaliacaoSuccess(payload));
    yield put(showSuccessMessage('regrasAvaliacao.sucessoExclusao'));
  } catch (error) {
    const mensagemErro = extractErrorMessage(error, 'Erro ao remover regra de avaliação');
    yield put(showMessage(mensagemErro));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnBuscaRegrasAvaliacaoEdicao({ payload }) {
  try {
    const id = payload;
    const retorno = yield call(getRegrasAvaliacaoEdicaoRequest, id);
    // Verifica se a resposta é um erro
    if (retorno && retorno.message && typeof retorno.message === 'string') {
      yield put(showMessage(retorno.message));
    } else {
      // A API pode retornar os dados diretamente ou dentro de retorno.data
      const data = retorno?.data || retorno;
      yield put(buscaRegrasAvaliacaoEdicaoSuccess(data));
    }
  } catch (error) {
    yield put(showMessage('Erro ao buscar regra de avaliação para edição'));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

export function* watchGetRegrasAvaliacao() {
  yield takeEvery(GET_REGRAS_AVALIACAO, getRegrasAvaliacao);
}

export function* watchAddRegrasAvaliacao() {
  yield takeEvery(ADD_REGRAS_AVALIACAO, addRegrasAvaliacao);
}

export function* watchUpdateRegrasAvaliacao() {
  yield takeEvery(UPDATE_REGRAS_AVALIACAO, updateRegrasAvaliacao);
}

export function* watchDeleteRegrasAvaliacao() {
  yield takeEvery(DELETE_REGRAS_AVALIACAO, deleteRegrasAvaliacao);
}

export function* watchBuscaRegrasAvaliacaoEdicao() {
  yield takeEvery(BUSCA_REGRAS_AVALIACAO_EDICAO, fnBuscaRegrasAvaliacaoEdicao);
}

export default function* rootSaga() {
  yield all([
    fork(watchGetRegrasAvaliacao),
    fork(watchAddRegrasAvaliacao),
    fork(watchUpdateRegrasAvaliacao),
    fork(watchDeleteRegrasAvaliacao),
    fork(watchBuscaRegrasAvaliacaoEdicao)
  ]);
}
